import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EVENT_WORLDS } from "../data/content";
import { SectionHeader, Reveal } from "./Ornament";
import { ArrowRight } from "lucide-react";

/** Horizontal snap-scroll carousel for Creative Event Worlds — inspired by
 *  the reference site's horizontal pin-scroll pattern. */
export default function EventWorldsScroller() {
  const trackRef = useRef(null);
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.6), behavior: "smooth" });
  };
  return (
    <section
      id="event-worlds"
      className="relative bg-[var(--lu-black)] section-y grain overflow-hidden"
      data-testid="event-worlds-scroller"
    >
      <div className="container-lux">
        <div className="flex items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-[var(--lu-gold)]" aria-hidden />
              <p className="kicker">Creative Event Worlds</p>
            </div>
            <Reveal>
              <h2 className="mt-6 h-editorial text-[46px] sm:text-[60px] lg:text-[80px] text-[var(--lu-ivory)] leading-[0.98]">themed worlds,<span className="italic text-[var(--lu-gold)]">built to live them.</span>
              </h2>
            </Reveal>
            <p className="mt-6 text-[16px] leading-[1.85] text-ivory-75 font-light max-w-xl">We don't just decorate events — we build entire universes
              that your guests will remember for years.</p>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <span className="font-micro text-[10px] text-ivory-70">Scroll</span>
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full border border-[var(--lu-gold)]/60 flex items-center justify-center hover:bg-[var(--lu-gold)] hover:text-[var(--lu-black)] text-[var(--lu-gold)] transition-all"
              data-testid="worlds-scroll-left"
              aria-label="Scroll left"
            >
              <ArrowRight size={16} className="rotate-180" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full border border-[var(--lu-gold)]/60 flex items-center justify-center hover:bg-[var(--lu-gold)] hover:text-[var(--lu-black)] text-[var(--lu-gold)] transition-all"
              data-testid="worlds-scroll-right"
              aria-label="Scroll right"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="snap-x-worlds flex gap-6 overflow-x-auto pb-4 -mx-6 lg:-mx-10 px-6 lg:px-10"
          data-testid="worlds-track"
        >
          {EVENT_WORLDS.map((w, i) => (
            <Link
              key={w.id}
              to={`/concepts/${w.id}`}
              className="tile shrink-0 w-[80vw] sm:w-[60vw] lg:w-[38rem] block"
              style={{ aspectRatio: "16 / 11" }}
              data-testid={`world-slide-${w.id}`}
            >
              <img src={w.image} alt={w.name} className="w-full h-full object-cover" />
              <div className="tile-cover-overlay" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8">
                <p className="kicker mb-2">World 0{i + 1}</p>
                <h3 className="font-serif text-[30px] lg:text-[40px] leading-[1.05] text-[var(--lu-ivory)]"
                  style={{ textShadow: "0 2px 14px rgba(0,0,0,0.8)" }}>
                  {w.name}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-ivory-80 max-w-md font-light">
                  {w.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
